import { Button } from '@mui/material'
import PropTypes from 'prop-types'
import { SERVER_ERROR_CODE_NAMES } from 'Queries/constants'
import { getCodeNameFromResponseError } from 'Queries/utils'
import React, { useContext } from 'react'
import { Else, If, Then } from 'react-if'
import { CONTENT_TYPES } from 'Scenes/constants'
import { navigateToDownloadPage, navigateToLoginModal } from 'Scenes/navigators'
import FAIcon from 'Theme/FAIcon'
import { FA_ICONS } from 'Theme/font-awesome-icon-constants'

import { HALO_INFINITE_GAME_ID } from '../../../constants'
import { useConnect3rdAccount, useConnected3rdAccounts } from '../../../hooks/use3rdAccounts'
import { useAuth } from '../../Auth'
import { NotificationContext } from '../../Notification/context'
import { ButtonBookmark } from '../Buttons/ButtonBookmark'

const REFRESH_TIME = 7 * 24 * 60 * 1000

const ContentBookmarkEntity = ({
  entityId,
  gameId,
  halowaypointId,
  halowaypointDeleted,
  isBookmarked,
  totalBookmarks,
  toggleBookmark,
  downloads,
  isLoading,
  type = CONTENT_TYPES.MAPS,
  publishedTime,
}) => {
  const { isAuthorized } = useAuth()
  const isHaloInfinite = gameId === HALO_INFINITE_GAME_ID && !!halowaypointId
  const isHaloLinkUnavailable = new Date() - new Date(publishedTime) < REFRESH_TIME
  const { notification } = useContext(NotificationContext)
  const { isXboxLiveConnected } = useConnected3rdAccounts()
  const { handleAccountConnect } = useConnect3rdAccount()

  const handleClick = () => {
    if (!isAuthorized) return navigateToLoginModal()
    if (!isHaloInfinite) return navigateToDownloadPage(type, entityId)
    if (!isXboxLiveConnected) {
      return handleAccountConnect()
    }

    return toggleBookmark(
      { id: entityId, queryType: isBookmarked ? 'delete' : 'post' },
      {
        onSuccess: () =>
          notification.success(isBookmarked ? 'Unbookmarked' : 'The map is now available on your Xbox account!'),
        onError: (error) => {
          if (getCodeNameFromResponseError(error) === SERVER_ERROR_CODE_NAMES.LIVE_LOGIN_REQUIRED) {
            notification.error('live token expired')
            handleAccountConnect()
          }
        },
      },
    )
  }

  return (
    <If condition={isHaloInfinite}>
      <Then>
        <If condition={halowaypointDeleted || !halowaypointId}>
          <Then>
            <Button
              variant="contained"
              color="error"
              disabled={!isHaloLinkUnavailable}
              endIcon={
                <FAIcon
                  iconClassName={FA_ICONS.EXCLAMATION}
                  boxSize={14}
                />
              }
              sx={{ padding: '8px 20px', minHeight: '40px' }}
            >
              {isHaloLinkUnavailable ? 'Link Broken' : 'No Longer Available'}
            </Button>
          </Then>
          <Else>
            <ButtonBookmark
              noLink={!halowaypointId}
              isBookmarked={isBookmarked}
              totalBookmarks={totalBookmarks}
              disabled={isLoading || !halowaypointId}
              onClick={handleClick}
              type={type}
            />
          </Else>
        </If>
      </Then>
      <Else>
        <ButtonBookmark
          noLink={!halowaypointId}
          totalBookmarks={downloads}
          isBookmarked={false}
          onClick={handleClick}
          disabled={isLoading || !halowaypointId}
          type={type}
        />
      </Else>
    </If>
  )
}

ContentBookmarkEntity.propTypes = {
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gameId: PropTypes.number,
  halowaypointId: PropTypes.string,
  halowaypointDeleted: PropTypes.bool,
  isBookmarked: PropTypes.bool,
  toggleBookmark: PropTypes.func,
  downloads: PropTypes.number,
  isLoading: PropTypes.bool,
  publishedTime: PropTypes.string,
  type: PropTypes.oneOf([CONTENT_TYPES.MAPS, CONTENT_TYPES.PREFABS]),
}

export default ContentBookmarkEntity
